import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import BigHeader from "../components/Elements/BigHeader";
import Spinner from "../components/Elements/Spinner";
import SizeSelector from "../components/SizeSelector";
import Descriptor from "../components/UnitListing/Descriptor";
import * as Constants from "../features/generalConstants";
import { formatSizes, getUnitSizes } from "../features/helpers";
import Location from "../features/rapidstor/entities/Location";
import {
  useGetReservationQuery,
  useGetUnitListQuery,
} from "../features/rapidstor/rapidstorApiSlice";
import { RapidStorReservationRequest } from "../services/rapidstor";
import store from "../store";
import {
  selectContactData,
  selectUnitData,
  setContactData,
  setReservationData,
  setReservationToken,
  setUnitData,
} from "../store/bookingSlice";
import classes from "./Quote.module.scss";
import { useCookies } from "react-cookie";
import moment from "moment";
import { Logger } from "../main";

function Quote() {
  const location = useLoaderData() as Location;
  const unitData = useSelector(selectUnitData);
  const navigate = useNavigate();
  const contactData = useSelector(selectContactData);
  const [sizes, setSizes] = useState<(string | number)[]>([]);
  const [descriptors, setDescriptors] = useState<Descriptor[]>([]);
  const [selectedUnitSize, setSelectedUnitSize] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState<boolean>(false);
  // RES TOKEN TEST 64e6aa4f4507ca75fa282f2b
  const { reservationTokenParams } = useParams();
  const [doFetchData, setDoFetchData] = useState<boolean>(true);
  const [doFetchReservation, setDoFetchReservation] = useState<boolean>(true);
  const [cookies, removeCookie] = useCookies(["user"]);

  const {
    data: reservation,
    isFetching: reservationFetching,
    isError: reservationError,
  } = useGetReservationQuery(
    {
      locationCode: location.locationCode,
      token: reservationTokenParams,
    },
    {
      skip: doFetchReservation || !reservationTokenParams,
    }
  );
  const { data: unitList, isFetching: unitListFetching } = useGetUnitListQuery(
    location.locationCode,
    {
      skip: doFetchData,
    }
  );

  useEffect(() => {
    if (cookies.user && cookies.user.locationCode != location.locationCode) {
      removeCookie("user", { url: "/" });
    }

    if (!contactData || !unitData) {
      if (!reservationTokenParams) {
        // Cookie initialised? Setup details from it.
        if (cookies.user) {
          const formData = {
            firstName: cookies.user.firstName,
            lastName: cookies.user.lastName,
            email: cookies.user.email,
            phone: cookies.user.phone,
            addr1: cookies.user.addr1,
            addr2: cookies.user.addr2,
            city: cookies.user.city,
            state: cookies.user.state,
            postalCode: cookies.user.postalCode,
            declaredInsuranceAmt: cookies.user.declaredInsuranceAmt,
          };
          store.dispatch(setContactData(formData));
        } else {
          navigate(`/${location.slug}`);
        }
      }
      setDoFetchReservation(false);
      if (!reservationFetching) {
        setDoFetchData(false);
        populateState();
      }
    } else {
      setupSlider();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationFetching, unitListFetching, unitData]);

  useEffect(() => {
    if (unitList) {
      const formattedSizes = formatSizes(unitList.descriptors);
      setSizes(getUnitSizes(formattedSizes.sortedList));
      let unitSizeIndex = 0;
      if (reservation) {
        unitSizeIndex = formattedSizes.formattedSizes.indexOf(reservation.unitType.area);
      } else if (cookies.user) {
        unitSizeIndex = formattedSizes.formattedSizes.indexOf(cookies.user.descriptor);
      }

      setDescriptors(formattedSizes.sortedList);
      const descriptors = unitList?.descriptors as Descriptor[];
      const unitSizes = formattedSizes.formattedSizes;
      const selectedDescriptor = descriptors[unitSizeIndex];
      const unitType = descriptors[unitSizeIndex].unitTypes[0];
      setSelectedUnitSize(unitSizeIndex);
      store.dispatch(
        setUnitData({
          location,
          unitType,
          unitSizes,
          descriptors,
          selectedDescriptor,
          reservation: false,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitList, reservation]);

  const setupSlider = () => {
    if (unitData) {
      if (unitData.descriptors && sizes.length === 0) {
        const sortedList: Descriptor[] = [];
        const sizes: (number | string)[] = [];
        unitData.descriptors.map((d, index) => {
          if (sizes.includes(d.area)) {
            return;
          }
          sizes.push(d.area);
          sortedList.push(unitData.descriptors[index]);
        });
        setSizes(unitData.unitSizes);
        setSelectedUnitSize(unitData.unitSizes.indexOf(unitData.selectedDescriptor.area));
        setDescriptors(sortedList);
      }
    }
  };

  const populateState = () => {
    if (reservation === null || reservationError) {
      if (!cookies.user) {
        navigate(`/${location.slug}`);
      }
    }
    if (unitList) {
      const formData = {
        firstName: reservation
          ? reservation.firstName
          : (cookies.user.firstName as string),
        lastName: reservation ? reservation.lastName : (cookies.user.lastName as string),
        email: reservation ? reservation.email : (cookies.user.email as string),
        phone: reservation ? reservation.phone : (cookies.user.phone as string),
        addr1: reservation ? reservation.addr1 : "",
        addr2: reservation ? reservation.addr2 : "",
        city: reservation ? reservation.city : "",
        state: reservation ? reservation.state : "",
        postalCode: reservation ? reservation.postalCode : "",
        declaredInsuranceAmt: reservation ? reservation.declaredInsuranceAmt : 0,
      };
      store.dispatch(setContactData(formData));
      const formattedSizes = formatSizes(unitList.descriptors);
      setSizes(getUnitSizes(formattedSizes.sortedList));
      setSelectedUnitSize(
        reservation ? formattedSizes.formattedSizes.indexOf(reservation.unitType.area) : 0
      );
      setDescriptors(formattedSizes.sortedList);
      const descriptors = unitList?.descriptors as Descriptor[];
      const unitSizes = formattedSizes.formattedSizes;
      const selectedDescriptor = descriptors[selectedUnitSize];
      const unitType = descriptors[selectedUnitSize].unitTypes[0];
      store.dispatch(
        setUnitData({
          location,
          unitType,
          unitSizes,
          descriptors,
          selectedDescriptor,
          reservation: false,
        })
      );
    }
  };

  const getCurrentUnitType = () => {
    for (const u of descriptors[selectedUnitSize].unitTypes) {
      if (u.hasComputedAvailable) {
        return u;
      }
    }
  };

  const createLead = (descriptor: Descriptor) => {
    const unitSizes = unitData?.unitSizes;
    const unitType = getCurrentUnitType();
    if (!unitType) {
      return null;
    }
    let neededDate = moment(reservation?.moveInContext.neededDate);
    if (neededDate.isBefore(moment())) {
      neededDate = moment().endOf("day");
    }
    const deal = unitType.deal?._id ? unitType.deal?._id : null;
    if (!unitSizes || !descriptors || !descriptor || !unitType || !contactData) {
      return null;
    }
    const reservationPayload: RapidStorReservationRequest = {
      locationCode: location.locationCode,
      payload: {
        customListing: true,
        enquiry: false,
        neededDate: neededDate.toISOString(),
        timezoneOffset: location.timezoneOffset,
        sourceUrl: window.location.href,
        tenant: {
          tenantId: null,
          firstName: contactData.firstName,
          lastName: contactData.lastName,
          email: contactData.email,
          phone: contactData.phone,
          addr1: contactData.addr1,
          addr2: contactData.addr2,
          city: contactData.city,
          state: contactData.state,
          postalCode: contactData.postalCode,
          declaredInsuranceAmt: contactData.declaredInsuranceAmt,
        },
        unitType: unitType._id,
        deal: deal,
        token: null,
      },
    };
    store.dispatch(setReservationData(reservationPayload));
    console.log("Storing reservation data in bookingSlice: ", reservationPayload);
  };

  const goToBookingConfirmation = async (descriptor: Descriptor) => {
    const unitSizes = unitData?.unitSizes;
    const descriptors = unitData?.descriptors;
    const unitType = getCurrentUnitType();
    const selectedDescriptor = descriptor;
    if (!unitType || !unitSizes || !descriptors || !contactData) {
      return null;
    }

    try {
      store.dispatch(
        setUnitData({ location, unitType, unitSizes, descriptors, selectedDescriptor })
      );
      if (reservationTokenParams) {
        store.dispatch(setReservationToken(reservationTokenParams));
      }
      createLead(descriptor);
      navigate(`/${location.slug}/confirm`);
    } catch (err) {
      Logger().error(`Error in goToBookingConfirmation: ${JSON.stringify(err)}`);
    }
  };

  const replaceText = (string: string, find: string, replace: string) => {
    return string.replace(find, replace);
  };

  if (!contactData) {
    return null;
  }

  return (
    <>
      <Row>
        <Col xs={12} sm={12} md={12}>
          <BigHeader>
            {replaceText(
              Constants.QUOTE_PAGE.HEADER_TEXT,
              "{{name}}",
              contactData.firstName
            )}
          </BigHeader>
          <div className={classes.other_options}>
            <button
              onClick={() => setSelectedUnitSize((prev) => (prev -= 1))}
              disabled={selectedUnitSize === 0}
            >
              {Constants.UNIT_SELECTOR.SMALLER_TEXT}
            </button>
            <button
              onClick={() => setSelectedUnitSize((prev) => (prev += 1))}
              disabled={selectedUnitSize === sizes.length - 1}
            >
              {Constants.UNIT_SELECTOR.BIGGER_TEXT}
            </button>
          </div>
          <SizeSelector
            sizes={sizes}
            selectedUnitSize={selectedUnitSize}
            setSelectedSizeState={setSelectedUnitSize}
          />
          {!loading &&
            descriptors.map((unit, index: number) => {
              if (unit.area == sizes[selectedUnitSize]) {
                return (
                  <div key={unit._id} className={classes.descriptor_wrapper}>
                    <Descriptor
                      onSelectSizeClicked={() =>
                        goToBookingConfirmation(descriptors[index] as Descriptor)
                      }
                      locationSlug={location.slug}
                      descriptor={descriptors[index]}
                      showDetails
                      showFacts
                      buttonText={Constants.QUOTE_PAGE.BUTTON_TEXT}
                    />
                  </div>
                );
              }
            })}
          {loading && <Spinner />}
        </Col>
      </Row>
    </>
  );
}

export default Quote;
